import React, { useState, useEffect, useMemo, ChangeEvent } from "react"
import { useTranslation, Trans } from "react-i18next"
import { User } from "@terveystalo/web-auth-onewelcome"

import {
  Button,
  Title,
  Lead,
  TextField,
  Icon,
  Select,
} from "@terveystalo/design-system-react-components"
import { Autocomplete } from "./components/Autocomplete"
import authClient, { authConfig } from "./UserManager"
import { getBackend, ToimipaikkaData, FeedbackIamValue } from "./client"
import { config } from "./config"
import { useFormPersistence } from "./hooks/useFormPersistence"

const client = getBackend()

interface FormElement {
  value: string
}

interface VaaratapahtumaFormProps {
  user: User | null
  isLoggedIn: boolean
  setHideNavLogin: (hide: boolean) => void
}

interface VaaratapahtumaFormState {
  description?: string
  toimipaikka?: string
  dateOfOccurrence?: string
  email?: string
  name?: string
  iam?: FeedbackIamValue
}

const vaaratapahtumaFormInitialState: VaaratapahtumaFormState = {
  description: "",
  toimipaikka: undefined,
  dateOfOccurrence: "",
  email: "",
  name: "",
  iam: undefined,
}

function VaaratapahtumaForm({ user: _user, isLoggedIn, setHideNavLogin }: VaaratapahtumaFormProps) {
  const { t, i18n } = useTranslation()
  const { formState, setFormState, isRestoring } = useFormPersistence({
    formKey: "vaaratapahtumaForm",
    initialState: vaaratapahtumaFormInitialState,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState("")
  const [toimipaikkaList, setToimipaikkaList] = useState<ToimipaikkaData[]>([])
  const [files, setFiles] = useState<File[]>([])
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  // Update page title when language changes
  React.useEffect(() => {
    document.title = t("pageTitleVaaratapahtuma")
  }, [i18n.language, t])

  // Debug logging for wrapper styles
  React.useEffect(() => {
    if (wrapperRef.current) {
      const computed = window.getComputedStyle(wrapperRef.current)
      console.log("VaaratapahtumaForm wrapper debug:", {
        width: computed.width,
        maxWidth: computed.maxWidth,
        marginLeft: computed.marginLeft,
        marginRight: computed.marginRight,
        classList: Array.from(wrapperRef.current.classList),
        viewportWidth: window.innerWidth,
      })
    }
  }, [])

  // Re-log on resize to see breakpoint changes
  React.useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        const computed = window.getComputedStyle(wrapperRef.current)
        console.log("VaaratapahtumaForm wrapper resize:", {
          width: computed.width,
          maxWidth: computed.maxWidth,
          viewportWidth: window.innerWidth,
        })
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Keep navigation authentication button visible for vaaratapahtuma form
  React.useEffect(() => {
    setHideNavLogin(false)
    return () => setHideNavLogin(false)
  }, [setHideNavLogin])

  // Disable scrolling when submit message modal is open
  React.useEffect(() => {
    if (submitMessage) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ""
    }
  }, [submitMessage])

  // Fetch toimipaikka list on component mount
  useEffect(() => {
    const fetchToimipaikka = async () => {
      try {
        const data = await client.getToimipaikka()

        if (data.data && data.data.length > 0) {
          console.log(`✓ Loaded ${data.data.length} locations for autocomplete`)
          setToimipaikkaList(data.data)
        } else {
          console.error("✗ Received empty location list from API")
          setToimipaikkaList([])
        }
      } catch (error) {
        console.error("✗ Failed to fetch locations:", error)
      }
    }
    fetchToimipaikka()
  }, [])

  // Create toimipaikka options from fetched list
  const toimipaikkaOptions = useMemo(() => {
    return [
      { label: t("vaaratapahtumaForm.toimipaikkaSelect"), value: "" },
      ...toimipaikkaList.map((toimipaikka) => {
        // Translate special toimipaikka names, fallback to original name if no translation exists
        const translationKey = `toimipaikkaNames.${toimipaikka.name}` as const
        const translatedName = t(translationKey, { defaultValue: toimipaikka.name })
        return {
          label: translatedName,
          value: toimipaikka.id,
        }
      }),
    ]
  }, [toimipaikkaList, t])

  // Create feedbackIam options (same as in FeedbackForm)
  const feedbackIamLabels: Record<FeedbackIamValue | "", string> = {
    "": t("feedbackIam.select"),
    [FeedbackIamValue.public_sector]: t("feedbackIam.public_sector"),
    [FeedbackIamValue.kela65]: t("feedbackIam.kela65"),
    [FeedbackIamValue.commitment_to_pay]: t("feedbackIam.commitment_to_pay"),
    [FeedbackIamValue.purchase_service_authorization]: t(
      "feedbackIam.purchase_service_authorization",
    ),
    [FeedbackIamValue.service_voucher]: t("feedbackIam.service_voucher"),
    [FeedbackIamValue.sickness_fund]: t("feedbackIam.sickness_fund"),
    [FeedbackIamValue.occupational_health_care]: t("feedbackIam.occupational_health_care"),
    [FeedbackIamValue.insurance]: t("feedbackIam.insurance"),
    [FeedbackIamValue.private]: t("feedbackIam.private"),
  }

  const feedbackIamOptions = Object.entries(feedbackIamLabels).map(([value, label]) => ({
    label,
    value,
  }))

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleSelectChange = (name: keyof VaaratapahtumaFormState) => (e: ChangeEvent) => {
    const value = (e.target as unknown as FormElement).value
    setFormState((prevState) => ({
      ...prevState,
      [name]: value === "" ? undefined : value,
    }))
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage("")

    // Validation
    if (!formState.description || formState.description.trim().length === 0) {
      setSubmitMessage(t("error.generic") + ": Description is required")
      setIsSubmitting(false)
      return
    }

    if (!formState.toimipaikka) {
      setSubmitMessage(t("error.toimipaikkaRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.dateOfOccurrence) {
      setSubmitMessage(t("error.visitDateRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.iam) {
      setSubmitMessage(t("error.generic") + ": Customer type is required")
      setIsSubmitting(false)
      return
    }

    if (!formState.email) {
      setSubmitMessage(t("error.emailRequired"))
      setIsSubmitting(false)
      return
    }

    // If not authenticated, name is required
    if (!isLoggedIn && (!formState.name || formState.name.trim().length === 0)) {
      setSubmitMessage(t("error.nameRequired"))
      setIsSubmitting(false)
      return
    }

    try {
      // Convert dateOfOccurrence string to Date object
      const dateOfOccurrence = formState.dateOfOccurrence
        ? new Date(formState.dateOfOccurrence)
        : new Date()

      // Check if we have files to upload (only for authenticated users)
      if (isLoggedIn && files.length > 0) {
        // Use FormData for multipart/form-data upload
        const formData = new FormData()

        // Append form fields
        formData.append("description", formState.description!)
        if (formState.toimipaikka) formData.append("toimipaikka", formState.toimipaikka)
        formData.append("dateOfOccurrence", dateOfOccurrence.toISOString())
        if (formState.email) formData.append("email", formState.email)
        if (formState.iam) formData.append("iam", formState.iam)

        // Append files
        files.forEach((file) => {
          formData.append("files", file)
        })

        // Make direct API call with FormData
        const backendUrl = config.backendHost
        const user = await authClient.getUser()
        const response = await fetch(`${backendUrl}/vaaratapahtuma`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${user?.access_token}`,
            ...(user?.id_token ? { "X-Id-Token": user.id_token } : {}),
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
      } else {
        // No files - use regular JSON submission
        if (isLoggedIn) {
          // Authenticated submission - exclude name field
          await client.submitVaaratapahtumaFeedback({
            description: formState.description!,
            toimipaikka: formState.toimipaikka!,
            dateOfOccurrence,
            email: formState.email!,
            iam: formState.iam as FeedbackIamValue,
          })
        } else {
          // Anonymous submission - include name field
          await client.submitAnonymousVaaratapahtumaFeedback({
            description: formState.description!,
            toimipaikka: formState.toimipaikka!,
            dateOfOccurrence,
            email: formState.email!,
            name: formState.name!,
            iam: formState.iam as FeedbackIamValue,
          })
        }
      }

      setSubmitMessage(t("success.feedbackSubmitted"))
      // Reset form
      setFormState(vaaratapahtumaFormInitialState)
      setFiles([]) // Clear files
    } catch (error) {
      console.error(error)
      setSubmitMessage(
        `${t("error.generic")}: ${error instanceof Error ? error.message : t("error.submitFailed")}`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show loading state while restoring form data
  if (isRestoring) {
    return (
      <div className="min-h-screen bg-coal04 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lake mx-auto mb-4"></div>
          <p className="text-coal65">{t("form.loading")}</p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="min-h-screen bg-coal04" style={{ position: "relative" }}>
        <div ref={wrapperRef} className="w-full m:max-w-7xl m:mx-auto">
          <header className="bg-woolhead shadow-sm border-b border-coal15">
            <div className="px-4 s:px-6 m:px-8 py-2">
              <Title>{t("vaaratapahtumaForm.title")}</Title>
              <Lead>
                <Trans
                  i18nKey="vaaratapahtumaForm.lead"
                  components={{
                    red: <span style={{ color: "#CE0F69" }} />,
                  }}
                />
              </Lead>
            </div>
          </header>
          <main className="s:px-6 m:px-8">
            <div
              className="bg-woolhead rounded-lg shadow p-2"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <Title as="h2" size="l" className="mb-2">
                {t("vaaratapahtumaForm.formInfo")}
              </Title>

              {/* Form fields */}
              <div className="space-y-2 mb-4">
                <div className="space-y-2 mb-4">
                  <Autocomplete
                    id="toimipaikka"
                    name="toimipaikka"
                    label={t("vaaratapahtumaForm.toimipaikkaLabel")}
                    placeholder={t("vaaratapahtumaForm.toimipaikkaPlaceholder")}
                    options={toimipaikkaOptions}
                    value={formState.toimipaikka || ""}
                    onChange={(value) => setFormState({ ...formState, toimipaikka: value })}
                    required
                  />
                </div>

                <TextField
                  id="dateOfOccurrence"
                  name="dateOfOccurrence"
                  type="date"
                  label={t("vaaratapahtumaForm.dateOfOccurrenceLabel")}
                  value={formState.dateOfOccurrence}
                  onChange={handleInputChange}
                  required
                />

                <div className="space-y-2 mb-4">
                  <Select
                    id="vaaratapahtuma-iam"
                    name="iam"
                    label={t("form.iam")}
                    options={feedbackIamOptions}
                    value={formState.iam || ""}
                    onChange={handleSelectChange("iam")}
                    required
                  />
                </div>

                <TextField
                  id="description"
                  name="description"
                  label={t("vaaratapahtumaForm.descriptionLabel")}
                  as="textarea"
                  value={formState.description}
                  onChange={handleInputChange}
                  rows={6}
                  required
                />

                {/* File upload - only shown for authenticated users */}
                {isLoggedIn && (
                  <div className="space-y-2 mb-4">
                    <label
                      htmlFor="file-upload-vaaratapahtuma"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("form.attachments")}
                    </label>
                    <input
                      id="file-upload-vaaratapahtuma"
                      name="files"
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                    {files.length > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        {t("form.filesSelected", { count: files.length })}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">{t("form.fileUploadHint")}</p>
                  </div>
                )}

                {/* Show Name field only for unauthenticated users */}
                {!isLoggedIn && (
                  <TextField
                    id="vaaratapahtuma-name"
                    name="name"
                    label={t("form.name")}
                    value={formState.name || ""}
                    onChange={handleInputChange}
                    required
                    placeholder={t("form.namePlaceholder")}
                  />
                )}

                <TextField
                  id="email"
                  name="email"
                  type="email"
                  label={t("vaaratapahtumaForm.emailLabel")}
                  value={formState.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <p className="text-coal65 mb-6">
                {t("form.privacyNotice")}
                <a href={t("form.privacyStatementUrl")} className="link">
                  {t("form.privacyStatement")}
                </a>
                {t("form.privacyCustomers")}
              </p>

              {/* Debugging block - only shown when DEBUG_FORM_VALUES is set */}
              {config.debugFormValues && (
                <pre className="p-4 bg-coal15 text-coal85 rounded-md my-4">
                  <code>{JSON.stringify(formState, null, 2)}</code>
                </pre>
              )}

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t("form.submitting") : t("submit")}
              </Button>
            </div>
          </main>
        </div>

        {/* Success/Error Modal */}
        {submitMessage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "32px",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                maxWidth: "500px",
                textAlign: "center",
              }}
            >
              <p style={{ marginBottom: "24px", fontSize: "1.125rem" }}>{submitMessage}</p>
              {isLoggedIn ? (
                <Button
                  type="button"
                  onClick={() =>
                    authClient.signoutRedirect({
                      post_logout_redirect_uri: authConfig.post_logout_redirect_uri,
                    })
                  }
                  variant="secondary"
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Icon name="LogOut" color="lake" />
                    {t("logout")}
                  </span>
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => (window.location.href = "https://www.terveystalo.com")}
                >
                  {t("close")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </form>
  )
}

export default VaaratapahtumaForm
